import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * LA CONFIRMATION D'UN LOT DE POSTES (copilote, `besoins_lot_creer`).
 *
 * « Le souci, c'est que parfois ce n'est pas des besoins, c'est plus des
 * viviers » ; « une liste en mode besoin ou vivier, et on doit choisir devant
 * chaque ligne » (13.10). La modale générique ne montrait qu'un texte à
 * accepter en bloc : ici chaque ligne se BASCULE (Besoin / Vivier) ou se
 * retire, et c'est la liste ajustée qui part à POST /agent/confirm — le
 * serveur la revérifie comme toujours (date, salaire, client, critères).
 *
 * Un besoin est un poste ouvert chez le client : client, date et salaire.
 * Un vivier est une colonne de profils à constituer : ni client, ni date ;
 * s'il a une annonce (Leman, LinkedIn), elle naît à valider.
 */
const BI = ({ icon, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} aria-hidden="true" />;

const plein = v => v !== undefined && v !== null && v !== '';
const fusion = (commun, l) => ({ ...commun, ...Object.fromEntries(Object.entries(l || {}).filter(([, v]) => plein(v))) });
const dateCH = v => { const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}.${m[2]}.${m[1]}` : String(v || ''); };
const chf = v => (Number.isFinite(Number(v)) && Number(v) > 0 ? `${Number(v).toLocaleString('fr-CH')} CHF` : String(v || ''));
const sansTexte = v => String(v.description || '').trim().length < 80;
const pluriel = (n, mot) => `${n} ${mot}${n > 1 ? 's' : ''}`;

// Ce que la ligne fera, dit en mots — rien d'autre que ce que la liste porte.
function details(cible, v) {
  if (cible === 'vivier') {
    const annonce = v.rediger_jd || v.publier_linkedin;
    return [
      !annonce ? 'colonne du Vivier seule' : sansTexte(v) ? 'annonce rédigée par Leman, sans client ni secteur, à valider' : 'annonce avec ce texte',
      v.publier_linkedin ? (sansTexte(v) ? 'LinkedIn à la validation' : 'LinkedIn') : null,
    ].filter(Boolean);
  }
  const leman = v.rediger_jd && sansTexte(v);
  return [
    plein(v.date_demarrage) ? `démarrage ${dateCH(v.date_demarrage)}` : null,
    plein(v.salaire_max) ? `max ${chf(v.salaire_max)}` : null,
    leman ? 'fiche par Leman, sans client ni secteur, à valider' : null,
    v.publier_linkedin ? `LinkedIn${leman ? ' à la validation' : ''}` : null,
    v.sourcing_auto ? 'recherche automatique' : null,
  ].filter(Boolean);
}

// Ce qui manque pour qu'une ligne soit un BESOIN (le serveur refuserait tout le lot).
function manquePourBesoin(v) {
  return [!plein(v.client) && 'client', !plein(v.date_demarrage) && 'date de démarrage', !plein(v.salaire_max) && 'salaire max'].filter(Boolean);
}

export default function LotConfirmation({ input, onResolve }) {
  const commun = useMemo(() => input?.commun || {}, [input]);
  const [lignes, setLignes] = useState(() => (Array.isArray(input?.lignes) ? input.lignes : []).map((brute, i) => ({
    i, brute, cible: brute?.cible === 'vivier' ? 'vivier' : 'besoin', garder: true,
  })));

  useEffect(() => {
    const precedent = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const echap = (e) => { if (e.key === 'Escape') { e.preventDefault(); onResolve(null); } };
    window.addEventListener('keydown', echap);
    return () => { document.body.style.overflow = precedent; window.removeEventListener('keydown', echap); };
  }, [onResolve]);

  const vues = lignes.map(x => ({ ...x, v: fusion(commun, x.brute) }));
  // Ce que « Pour toutes les lignes » dit déjà n'est pas répété sur chaque besoin.
  const dejaDit = details('besoin', commun);
  const propre = x => details(x.cible, x.v).filter(d => x.cible === 'vivier' || !dejaDit.includes(d));
  const gardees = vues.filter(x => x.garder);
  const nbBesoins = gardees.filter(x => x.cible === 'besoin').length;
  const nbViviers = gardees.length - nbBesoins;
  const retirees = vues.length - gardees.length;
  const incompletes = gardees.filter(x => x.cible === 'besoin' && manquePourBesoin(x.v).length);
  // Deux lignes vivier au même intitulé partagent UNE colonne (et une annonce).
  const partagees = (() => {
    const vus = new Map();
    for (const x of gardees.filter(y => y.cible === 'vivier')) {
      const k = String(x.v.titre || '').trim().toLowerCase();
      vus.set(k, [...(vus.get(k) || []), x.v.titre]);
    }
    return [...vus.values()].filter(t => t.length > 1).map(t => t[0]);
  })();

  const poser = (i, patch) => setLignes(ls => ls.map(x => (x.i === i ? { ...x, ...patch } : x)));
  const toutEn = (cible) => setLignes(ls => ls.map(x => (cible === 'besoin' && !plein(fusion(commun, x.brute).client) ? x : { ...x, cible })));
  const confirmer = () => onResolve({ ...input, lignes: gardees.map(x => ({ ...x.brute, cible: x.cible })) });

  const lk = commun.publier_linkedin ? `LinkedIn${Array.isArray(commun.linkedin_criteres) && commun.linkedin_criteres.length ? ` (${pluriel(commun.linkedin_criteres.length, 'critère')})` : ''}` : null;
  const communTexte = [
    plein(commun.date_demarrage) && `démarrage ${dateCH(commun.date_demarrage)}`,
    plein(commun.salaire_max) && `max ${chf(commun.salaire_max)}`,
    commun.rediger_jd && 'textes par Leman, sans client ni secteur, à valider',
    lk && `${lk} à la validation`,
  ].filter(Boolean);

  // Le dossier AO du lot (14.10) : créé s'il n'existe pas, les BESOINS y
  // sont rattachés — dit avant le clic, et recompté quand une ligne bascule.
  const ao = (() => { const a = input?.ao ?? commun.ao; const t = typeof a === 'string' ? a : a?.titre; return t ? String(t) : null; })();

  return createPortal(
    <div className="erp-dialog-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onResolve(null); }}>
      <section className="erp-dialog erp-dialog--lot" role="dialog" aria-modal="true" aria-labelledby="lot-titre">
        <header className="erp-dialog__header">
          <span className="erp-dialog__icon"><BI icon="list-check" /></span>
          <div className="erp-dialog__heading">
            <h2 id="lot-titre">Créer {pluriel(gardees.length, 'ligne')}</h2>
            <p>Devant chaque ligne : un besoin (un poste ouvert chez le client) ou un vivier (des profils à constituer).</p>
          </div>
          <button type="button" className="erp-dialog__close" onClick={() => onResolve(null)} aria-label="Fermer"><BI icon="x-lg" /></button>
        </header>

        <div className="lot-barre">
          <span className="lot-compte" aria-live="polite">
            <strong>{nbBesoins}</strong> besoin{nbBesoins > 1 ? 's' : ''} · <strong>{nbViviers}</strong> vivier{nbViviers > 1 ? 's' : ''}
            {retirees ? <> · {pluriel(retirees, 'retirée')}</> : null}
          </span>
          <span className="lot-tout">
            Tout en
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => toutEn('besoin')}>besoins</button>
            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => toutEn('vivier')}>viviers</button>
          </span>
        </div>
        {communTexte.length > 0 && <p className="lot-commun"><span>Pour toutes les lignes</span> {communTexte.join(' · ')}</p>}
        {ao && (
          <p className="lot-commun"><span>Dossier AO</span> « {ao} » — créé s'il n'existe pas ; {nbBesoins > 1 ? `les ${nbBesoins} besoins y sont rattachés` : nbBesoins === 1 ? 'le besoin y est rattaché' : 'aucun besoin à y rattacher'} (jamais les viviers)</p>
        )}

        <ul className="lot-liste">
          {vues.map(x => {
            const manque = manquePourBesoin(x.v);
            const sansClient = !plein(x.v.client);
            return (
              <li key={x.i} className={`lot-ligne${x.garder ? '' : ' is-retiree'}`}>
                <input type="checkbox" className="lot-garder" checked={x.garder} onChange={() => poser(x.i, { garder: !x.garder })}
                  aria-label={`${x.garder ? 'Retirer' : 'Remettre'} « ${x.v.titre} »`} />
                <div className="lot-quoi">
                  <span className="lot-titre">{x.v.titre || 'Sans intitulé'}</span>
                  <span className="lot-client">
                    {x.cible === 'besoin' ? (x.v.client || 'client à préciser') : `colonne du Vivier${x.v.client ? ` — ${x.v.client} n'y apparaît pas` : ''}`}
                  </span>
                  {x.garder && propre(x).length > 0 && <span className="lot-details">{propre(x).join(' · ')}</span>}
                  {x.garder && x.cible === 'besoin' && manque.length > 0 && <span className="lot-manque"><BI icon="exclamation-triangle" /> manque : {manque.join(', ')}</span>}
                </div>
                <div className="lot-choix" role="radiogroup" aria-label={`« ${x.v.titre} » : besoin ou vivier`}>
                  <button type="button" role="radio" aria-checked={x.cible === 'besoin'} className="lot-choix__btn"
                    disabled={!x.garder || sansClient} title={sansClient ? 'Un besoin demande un client' : undefined}
                    onClick={() => poser(x.i, { cible: 'besoin' })}>Besoin</button>
                  <button type="button" role="radio" aria-checked={x.cible === 'vivier'} className="lot-choix__btn"
                    disabled={!x.garder} onClick={() => poser(x.i, { cible: 'vivier' })}>Vivier</button>
                </div>
              </li>
            );
          })}
        </ul>

        {(incompletes.length > 0 || partagees.length > 0) && (
          <div className="lot-notes">
            {incompletes.length > 0 && (
              <p className="lot-note lot-note--bloque"><BI icon="exclamation-triangle" /> {pluriel(incompletes.length, 'ligne')} en besoin sans tout ce qu'il faut ({incompletes.slice(0, 3).map(x => `« ${x.v.titre} »`).join(', ')}{incompletes.length > 3 ? '…' : ''}) : basculez-les en vivier, retirez-les, ou demandez au copilote de compléter.</p>
            )}
            {partagees.length > 0 && (
              <p className="lot-note"><BI icon="info-circle" /> {partagees.map(t => `« ${t} »`).join(', ')} : plusieurs lignes vivier au même intitulé partagent une seule colonne, et une seule annonce.</p>
            )}
          </div>
        )}

        <footer className="erp-dialog__actions lot-pied">
          <span className="lot-pied__note">Rien n'est créé avant ce clic.</span>
          <button type="button" className="erp-btn erp-btn--secondary" onClick={() => onResolve(null)}>Annuler</button>
          <button type="button" className="erp-btn erp-btn--primary" disabled={!gardees.length || incompletes.length > 0} onClick={confirmer} autoFocus>
            <BI icon="check-lg" /> Créer {pluriel(gardees.length, 'ligne')}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
